import React from 'react';
import { styled } from 'components/ThemeConfigConnector/ThemeConfigConnnector';
import { LoaderWrapper, LoadingRectangle } from 'style/styled-components/reusable.css';
import { PageHeader, DetailsWrapper, ImageWrapper } from 'components/Pages/PageDetailHeader/PageDetailsHeader.css';
import { Breakpoints, MQ } from 'style/styled-components/cssMediaQueries';

const LoadingElementWrapper = styled.div<{ width: string; height: string; borderRadius: string }>`
    height: ${(props) => props.height};
    width: ${(props) => props.width};
    margin-left: auto;
    margin-right: auto;
    margin-top: 14px;
    border-radius: ${(props) => props.borderRadius};
    overflow: hidden;

    ${MQ(Breakpoints.xs)} {
        margin-left: 0;
        margin-right: 0;
    }
`;

const LogoLoadingWrapper = styled.div`
    height: 32px;
    width: 57px;
    margin-left: auto;
    margin-right: auto;
    top: 10px;
    right: 8px;
    border-radius: 30px;
    overflow: hidden;

    ${MQ(Breakpoints.xs)} {
        position: static;
        margin-left: 0;
    }
`;

const TitleLoadingWrapper = styled.div`
    height: 24px;
    width: 93%;
    margin: 14px auto 7px;
    border-radius: 30px;
    overflow: hidden;

    ${MQ(Breakpoints.xs)} {
        width: 320px;
        margin-left: 0;
    }
`;

const SubtitleLoadingWrapper = styled.div`
    margin-left: 0;

    ${MQ(Breakpoints.xs)} {
        & > div:first-child {
            width: 219px;
        }

        & > div:nth-child(2) {
            width: 256px;
        }
    }
`;

const MetaDataLoadingWrapperMobile = styled.div`
    margin-top: 16px;

    ${MQ(Breakpoints.xs)} {
        display: none;
    }
`;

const MetaDataLoadingWrapperDesktop = styled.div`
    margin-top: 24px;
    display: none;

    ${MQ(Breakpoints.xs)} {
        display: block;
    }
`;

const ButtonsLoadingWrapperMobile = styled.div`
    margin-top: 16px;
    display: flex;
    justify-content: space-around;
    align-items: center;
    gap: 32px;

    ${MQ(Breakpoints.xs)} {
        display: none;
    }
`;

const ButtonsLoadingWrapperDesktop = styled.div`
    margin-top: 24px;
    display: none;
    align-items: center;
    gap: 32px;

    ${MQ(Breakpoints.xs)} {
        display: flex;
    }
`;

const DescriptionLoadingWrapperMobile = styled.div`
    margin-top: 24px;

    & > div {
        margin-left: 0;
        margin-right: 0;
    }

    ${MQ(Breakpoints.xs)} {
        display: none;
    }
`;

const EpisodesLoadingWrapperMobile = styled.div<{ isSeries: boolean }>`
    margin-top: 24px;
    margin-right: ${(props) => (props.isSeries ? '0' : '-16px')};

    & > div {
        margin-left: 0;
        margin-right: 0;
    }

    ${MQ(Breakpoints.xs)} {
        display: none;
    }
`;

const LoadingEpisodesWrapper = styled.div`
    margin-top: 51px;
    display: none;
    padding-left: 32px;

    ${MQ(Breakpoints.xs)} {
        display: block;
    }

    ${MQ(Breakpoints.m)} {
        padding-left: 72px;
    }
`;

const ButtonMobileWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const FlexContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 16px;
    overflow: hidden;
`;

const LoadingCardWrapper = styled.div<{ isSeries: boolean }>`
    flex: 1;
    min-width: ${(props) => (props.isSeries ? '272px' : '176px')};
    height: ${(props) => (props.isSeries ? '153px' : '264px')};
    border-radius: 0;
    overflow: hidden;
`;

const SeasonFlexContainer = styled(FlexContainer)`
    gap: 36px;
`;

const EpisodeListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: 16px;

    ${MQ(Breakpoints.m)} {
        display: none;
    }
`;

const SeriesHorizontalContainer = styled.div`
    display: none;

    ${MQ(Breakpoints.m)} {
        display: block;
    }
`;

const EpisodeRowWrapper = styled.div`
    display: flex;
    gap: 16px;
    align-items: flex-start;
`;

const EpisodeThumbnailWrapper = styled.div`
    min-width: 272px;
    height: 153px;
    border-radius: 4px;
    overflow: hidden;
`;

const EpisodeTextWrapper = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;

    & > div {
        margin-left: 0;
        margin-right: 0;
    }
`;

export const ProgramDetailLoadingWrapper = styled.div`
    width: 100%;
`;

interface Props {
    isSeries: boolean;
}

export const PageDetailLoading = ({ isSeries }: Props) => (
    <ProgramDetailLoadingWrapper className={'top-aligned'}>
        <LoaderWrapper>
            <PageHeader>
                <ImageWrapper />
                <DetailsWrapper>
                    <LogoLoadingWrapper>
                        <LoadingRectangle />
                    </LogoLoadingWrapper>
                    <TitleLoadingWrapper>
                        <LoadingRectangle />
                    </TitleLoadingWrapper>
                    <SubtitleLoadingWrapper>
                        <LoadingElementWrapper width={'64%'} height={'20px'} borderRadius={'30px'}>
                            <LoadingRectangle />
                        </LoadingElementWrapper>
                        <LoadingElementWrapper width={'75%'} height={'12px'} borderRadius={'30px'}>
                            <LoadingRectangle />
                        </LoadingElementWrapper>
                    </SubtitleLoadingWrapper>
                    <MetaDataLoadingWrapperMobile>
                        <LoadingElementWrapper width={'95%'} height={'40px'} borderRadius={'4px'}>
                            <LoadingRectangle />
                        </LoadingElementWrapper>
                    </MetaDataLoadingWrapperMobile>
                    <MetaDataLoadingWrapperDesktop>
                        <LoadingElementWrapper width={'400px'} height={'12px'} borderRadius={'30px'}>
                            <LoadingRectangle />
                        </LoadingElementWrapper>
                        <LoadingElementWrapper width={'320px'} height={'12px'} borderRadius={'30px'}>
                            <LoadingRectangle />
                        </LoadingElementWrapper>
                    </MetaDataLoadingWrapperDesktop>
                    <ButtonsLoadingWrapperMobile>
                        {[...Array(3)].map((_, index) => (
                            <ButtonMobileWrapper key={`button-mobile-${index}`}>
                                <LoadingElementWrapper width={'24px'} height={'24px'} borderRadius={'50%'}>
                                    <LoadingRectangle />
                                </LoadingElementWrapper>
                                <LoadingElementWrapper width={'32px'} height={'8px'} borderRadius={'30px'}>
                                    <LoadingRectangle />
                                </LoadingElementWrapper>
                            </ButtonMobileWrapper>
                        ))}
                    </ButtonsLoadingWrapperMobile>
                    <DescriptionLoadingWrapperMobile>
                        <LoadingElementWrapper width={'93%'} height={'12px'} borderRadius={'30px'}>
                            <LoadingRectangle />
                        </LoadingElementWrapper>
                        <LoadingElementWrapper width={'70%'} height={'12px'} borderRadius={'30px'}>
                            <LoadingRectangle />
                        </LoadingElementWrapper>
                        <LoadingElementWrapper width={'93%'} height={'12px'} borderRadius={'30px'}>
                            <LoadingRectangle />
                        </LoadingElementWrapper>
                    </DescriptionLoadingWrapperMobile>
                    <EpisodesLoadingWrapperMobile isSeries={isSeries}>
                        <LoadingElementWrapper width={'40%'} height={isSeries ? '40px' : '16px'} borderRadius={isSeries ? '4px' : '30px'}>
                            <LoadingRectangle />
                        </LoadingElementWrapper>
                        {isSeries ? (
                            <>
                                <LoadingElementWrapper width={'100%'} height={'153px'} borderRadius={'4px'}>
                                    <LoadingRectangle />
                                </LoadingElementWrapper>
                                <LoadingElementWrapper width={'30%'} height={'12px'} borderRadius={'30px'}>
                                    <LoadingRectangle />
                                </LoadingElementWrapper>
                            </>
                        ) : (
                            <FlexContainer>
                                {[...Array(12)].map((_, index) => (
                                    <LoadingCardWrapper key={`mobile-asset-${index}`} isSeries={isSeries}>
                                        <LoadingRectangle />
                                    </LoadingCardWrapper>
                                ))}
                            </FlexContainer>
                        )}
                    </EpisodesLoadingWrapperMobile>
                    <ButtonsLoadingWrapperDesktop>
                        <LoadingElementWrapper width={'120px'} height={'40px'} borderRadius={'4px'}>
                            <LoadingRectangle />
                        </LoadingElementWrapper>
                        {[...Array(3)].map((_, index) => (
                            <LoadingElementWrapper key={`button-small-${index}`} width={'24px'} height={'24px'} borderRadius={'50%'}>
                                <LoadingRectangle />
                            </LoadingElementWrapper>
                        ))}
                    </ButtonsLoadingWrapperDesktop>
                </DetailsWrapper>
            </PageHeader>
            <LoadingEpisodesWrapper>
                {isSeries ? (
                    <SeasonFlexContainer>
                        <LoadingElementWrapper width={'83px'} height={'16px'} borderRadius={'30px'}>
                            <LoadingRectangle />
                        </LoadingElementWrapper>
                        {[...Array(12)].map((_, index) => (
                            <LoadingElementWrapper key={`season-selector-${index}`} width={'16px'} height={'16px'} borderRadius={'50%'}>
                                <LoadingRectangle />
                            </LoadingElementWrapper>
                        ))}
                    </SeasonFlexContainer>
                ) : (
                    <LoadingElementWrapper width={'120px'} height={'16px'} borderRadius={'30px'}>
                        <LoadingRectangle />
                    </LoadingElementWrapper>
                )}
                {isSeries ? (
                    <>
                        <EpisodeListContainer>
                            {[...Array(4)].map((_, index) => (
                                <EpisodeRowWrapper key={`episode-row-${index}`}>
                                    <EpisodeThumbnailWrapper>
                                        <LoadingRectangle />
                                    </EpisodeThumbnailWrapper>
                                    <EpisodeTextWrapper>
                                        <LoadingElementWrapper width={'40%'} height={'12px'} borderRadius={'30px'}>
                                            <LoadingRectangle />
                                        </LoadingElementWrapper>
                                        <LoadingElementWrapper width={'100%'} height={'12px'} borderRadius={'30px'}>
                                            <LoadingRectangle />
                                        </LoadingElementWrapper>
                                        <LoadingElementWrapper width={'100%'} height={'12px'} borderRadius={'30px'}>
                                            <LoadingRectangle />
                                        </LoadingElementWrapper>
                                        <LoadingElementWrapper width={'50%'} height={'12px'} borderRadius={'30px'}>
                                            <LoadingRectangle />
                                        </LoadingElementWrapper>
                                    </EpisodeTextWrapper>
                                </EpisodeRowWrapper>
                            ))}
                        </EpisodeListContainer>
                        <SeriesHorizontalContainer>
                            <FlexContainer>
                                {[...Array(12)].map((_, index) => (
                                    <LoadingCardWrapper key={`asset-${index}`} isSeries={isSeries}>
                                        <LoadingRectangle />
                                    </LoadingCardWrapper>
                                ))}
                            </FlexContainer>
                        </SeriesHorizontalContainer>
                    </>
                ) : (
                    <FlexContainer>
                        {[...Array(12)].map((_, index) => (
                            <LoadingCardWrapper key={`asset-${index}`} isSeries={isSeries}>
                                <LoadingRectangle />
                            </LoadingCardWrapper>
                        ))}
                    </FlexContainer>
                )}
            </LoadingEpisodesWrapper>
        </LoaderWrapper>
    </ProgramDetailLoadingWrapper>
);
